import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { FAQSchema, BreadcrumbSchema } from '../../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../../components/seo/Breadcrumbs';
import { LatestArticles } from '../../components/blog/LatestArticles';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { MapPin, Clock, Phone, Music, Wine, Crown, ArrowRight } from 'lucide-react';

export default function NightClubJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "What are the opening hours of Scratch Unleashed?",
      answer: "Scratch Unleashed is open to the public Friday through Sunday from 6 PM until late night. We're closed for regular operations Monday to Thursday, but available for private party bookings on weekdays. Contact us to arrange weekday private events."
    },
    {
      question: "What is the entry fee for Scratch Unleashed?",
      answer: "Entry fees vary by event and day. Regular weekend nights start from ₹1500 per person, while special DJ nights and events range from ₹2000-₹3000. VIP table bookings include entry for your group. Check our current events for specific pricing."
    },
    {
      question: "What is the dress code for the night club?",
      answer: "We enforce a smart casual dress code. Men should wear collared shirts or stylish casual wear with proper shoes. No sportswear, flip-flops, or torn clothing. Ladies can wear dresses, skirts, or smart casual attire. We reserve the right to deny entry based on inappropriate attire."
    },
    {
      question: "How do I book a table at Scratch Unleashed?",
      answer: "You can reserve tables directly through our website by clicking 'Reserve Table' or calling our booking hotline. We recommend booking at least 48 hours in advance, especially for weekends and special events. Group bookings and birthday packages available."
    },
    {
      question: "Is Scratch Unleashed suitable for birthday parties?",
      answer: "Absolutely! Scratch Unleashed is Jamshedpur's premier birthday party venue. We offer customized packages including private tables, bottle service, dedicated hosts, and special birthday arrangements. We're also available for private weekday bookings for larger celebrations."
    }
  ];

  const breadcrumbItems = [
    { name: 'Night Clubs in Jamshedpur', url: null }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="Night Club in Jamshedpur | Scratch Unleashed - Premium Nightlife"
        description="Experience the best night club in Jamshedpur. Scratch Unleashed offers world-class DJs, VIP tables, premium bottle service. Located near Sonari & Bindal Mall. Book now!"
        keywords="night club in Jamshedpur, best night club Jamshedpur, clubs near Sonari, Bindal Mall nightlife, premium nightclub Jamshedpur, party place Jamshedpur"
        canonical="https://scratchjsr.com/night-club-in-jamshedpur"
      />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'Night Clubs in Jamshedpur', url: 'https://scratchjsr.com/night-club-in-jamshedpur' }
      ]} />

      {/* Header Spacer */}
      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Hero Section */}
        <div className="mb-12">
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            NIGHT CLUB IN JAMSHEDPUR
          </h1>
          <p className="text-xl text-[var(--text-dim)] mb-6">
            Experience Premium Nightlife at Scratch Unleashed
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
              Reserve Your Table
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/events')}>
              View Upcoming Events
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Best Night Club in Jamshedpur</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Looking for the ultimate <strong>night club in Jamshedpur</strong>? Scratch Unleashed stands as Jamshedpur's premier nightlife destination, offering an unparalleled party experience in the heart of the city. Located conveniently near Sonari and Bindal Mall, we've redefined what it means to experience premium nightlife in Jamshedpur.
                </p>
                <p>
                  As the <strong>best night club in Jamshedpur</strong>, Scratch Unleashed brings together world-class DJs, state-of-the-art sound systems, premium bottle service, and an atmosphere that's both electric and exclusive. Whether you're celebrating a special occasion, enjoying a night out with friends, or looking to experience the finest DJ nights in Jamshedpur, Scratch Unleashed delivers an unforgettable experience every single time.
                </p>
              </div>
            </section>

            {/* What Makes Us Different */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Why Scratch Unleashed?</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  When searching for <strong>clubs in Jamshedpur</strong>, you'll find that Scratch Unleashed offers a unique combination of luxury, energy, and world-class entertainment. Our venue features multiple zones including a main dance floor with premium lighting, VIP lounges with private seating, and a rooftop area perfect for those who prefer a more relaxed atmosphere while still enjoying the pulse of the night.
                </p>
                <p>
                  Our commitment to delivering the best <strong>nightlife in Jamshedpur</strong> extends beyond just the music. We've invested in Funktion-One sound systems that deliver crystal-clear audio quality, ensuring every beat hits exactly as the artist intended. Our lighting design transforms throughout the night, creating an immersive visual experience that complements our musical programming perfectly.
                </p>
                <p>
                  As a <strong>party place in Jamshedpur</strong>, we understand that great nightlife is about more than just the venue—it's about the crowd, the energy, and the memories created. That's why we carefully curate our events, bringing in both national and international DJs who specialize in house, techno, hip-hop, and commercial music. Our door policy ensures a sophisticated, fashionable crowd that elevates the entire experience.
                </p>
              </div>
            </section>

            {/* Location & Accessibility */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Perfectly Located in Jamshedpur</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  Scratch Unleashed's strategic location makes us easily accessible from all major areas of Jamshedpur. Situated near the popular Sonari area and close to Bindal Mall, we're at the epicenter of Jamshedpur's entertainment district. Whether you're coming from Bistupur, Kadma, or Mango, reaching our venue is convenient and straightforward.
                </p>
                <p>
                  For those searching for <strong>clubs near Sonari</strong> or <strong>Bindal Mall nightlife</strong>, Scratch Unleashed offers the perfect combination of accessibility and premium experience. Our location provides ample parking space and is well-connected by road, making it easy for you and your friends to arrive safely and leave at your convenience.
                </p>
              </div>
            </section>

            {/* Services & Offerings */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Premium Services & Offerings</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[var(--text)]">VIP Table Service</h3>
                  <p className="text-[var(--text-dim)]">Experience the ultimate in luxury with our VIP table booking. Enjoy priority entry, dedicated hosts, premium bottle service, and the best views of our DJ booth and dance floor. Perfect for special celebrations or when you want to elevate your night out.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[var(--text)]">Premium Bar</h3>
                  <p className="text-[var(--text-dim)]">Our mixologists craft signature cocktails using premium spirits and fresh ingredients. From classic favorites to innovative creations, our bar menu is designed to complement your night with sophisticated flavors.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[var(--text)]">World-Class DJs</h3>
                  <p className="text-[var(--text-dim)]">We regularly host renowned DJs from across India and international artists who bring their unique sound to Jamshedpur. Our music programming covers house, techno, hip-hop, Bollywood, and commercial hits, ensuring there's something for everyone.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[var(--text)]">Private Events</h3>
                  <p className="text-[var(--text-dim)]">Looking for a <strong>birthday party venue in Jamshedpur</strong>? Scratch Unleashed offers customized packages for birthdays, corporate events, and private celebrations. Our events team will work with you to create a memorable experience tailored to your needs.</p>
                </div>
              </div>
            </section>

            {/* Call to Action */}
            <section className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8">
              <h2 className="font-bebas text-3xl mb-4">Experience Jamshedpur's Best Night Club</h2>
              <p className="text-[var(--text-dim)] mb-6">
                Don't just take our word for it—experience what makes Scratch Unleashed the premier night club in Jamshedpur. Whether you're a regular on the nightlife scene or looking to try something new, we promise an unforgettable night.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
                  Reserve Your Table Now
                </Button>
                <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/vip-table-booking-jamshedpur')}>
                  Explore VIP Packages
                </Button>
              </div>
            </section>

            {/* FAQ Section */}
            <section>
              <h2 className="font-bebas text-3xl mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
                    <AccordionTrigger className="hover:no-underline text-left">
                      <span className="font-semibold">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Info Card */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Quick Info</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Opening Hours</p>
                    <p className="text-sm text-[var(--text-dim)]">Fri-Sun: 6 PM - Late</p>
                    <p className="text-xs text-[var(--text-muted)] mt-1">Weekdays: Private parties only</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Location</p>
                    <p className="text-sm text-[var(--text-dim)]">Near Sonari, Bindal Mall Area, Jamshedpur</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Reservations</p>
                    <p className="text-sm text-[var(--text-dim)]">Click Reserve Table to book</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Google Map */}
            <Card className="p-0 overflow-hidden bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <div className="aspect-square">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3666.826790556041!2d86.19787467526635!3d22.805879979362773!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f5e3bc0f055ce3%3A0x94fae8cb5f18ece8!2sScratch%20Unleashed%20Bar%20%26%20Kitchen!5e0!3m2!1sen!2sin!4v1712345678901!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Scratch Unleashed Location - Bindal Mall, Sonari, Jamshedpur"
                ></iframe>
              </div>
            </Card>

            {/* Latest Articles */}
            <LatestArticles maxPosts={3} />

            {/* Related Pages */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Explore More</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/dj-night-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → DJ Nights in Jamshedpur
                </button>
                <button
                  onClick={() => navigate('/vip-table-booking-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → VIP Table Booking
                </button>
                <button
                  onClick={() => navigate('/birthday-party-venue-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → Birthday Party Venue
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
